<script setup lang="ts">
import { twMerge } from 'tailwind-merge';

import { computeFees, FeeTarget } from '@lib/shared';
import type { PropType } from 'vue';

import type { FeeOverrideEntity } from '~/apiClient';

const { isBuyer, baseAmount, assetsCount, feeConfiguration, referenceDate, wrapperClass } = defineProps({
    isBuyer: { type: Boolean, required: true },
    baseAmount: { type: Number, required: true },
    assetsCount: { type: Number, required: true },
    referenceDate: { type: String as PropType<string | null>, default: undefined },
    feeConfiguration: { type: Object as PropType<FeeOverrideEntity | null>, default: undefined },
    wrapperClass: { type: String, default: undefined },
});

const { tn, tt } = useTypedI18n();

const feeTarget = computed((): FeeTarget => (isBuyer ? FeeTarget.Buyer : FeeTarget.Seller));

const fees = computed(() =>
    computeFees({
        feeTarget: feeTarget.value,
        baseAmount,
        assetsCount,
        referenceDate: referenceDate ? new Date(referenceDate) : undefined,
        feeConfiguration,
    })
);

const grossFeeAmount = computed((): number => (isBuyer ? fees.value.grossTotal : fees.value.grossNet));
const feeAmount = computed((): number => (isBuyer ? fees.value.total : fees.value.net));

const isDiscounted = computed((): boolean => feeAmount.value < grossFeeAmount.value);

const mergedWrapperClass = twMerge('text-sm font-medium text-center', wrapperClass);
</script>

<template>
    <p :class="mergedWrapperClass">
        <span>{{ tt(`fees.description.${feeTarget}`) }}:&nbsp;</span>
        <span class="inline-flex items-center justify-center gap-1" :class="{ 'line-through': isDiscounted, 'text-primary': !isDiscounted }">
            {{ tn(isDiscounted ? grossFeeAmount : feeAmount, 'currency') }}
            <SaleFeesInfoModalButton />
        </span>
    </p>
    <p v-if="isDiscounted" :class="mergedWrapperClass">
        <span class="font-bold">{{ tt('fees.blackWeekDescription') }}:&nbsp;</span>
        <span class="text-primary">{{ tn(feeAmount, 'currency') }}</span>
    </p>
</template>
